import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import { AlertTriangle, Clock, Calendar, Users, CreditCard, FileCheck, Send, RefreshCw, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AlertsExpiringContracts({ getAuthHeader }) {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState(30);
  const [typeFilter, setTypeFilter] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    loadExpiringContracts();
  }, [daysFilter, typeFilter]);

  const loadExpiringContracts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ days: daysFilter });
      if (typeFilter) params.append('type_filter', typeFilter);
      
      const response = await axios.get(
        `${API}/api/alerts/expiring-contracts?${params}`,
        { headers: getAuthHeader() }
      );
      
      setContracts(response.data.expiring_contracts || []);
      setStats(response.data.stats);
    } catch {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (contractId, contractType) => {
    setSendingReminder(contractId);
    try {
      await axios.post(
        `${API}/api/alerts/send-renewal-reminder/${contractId}?contract_type=${contractType}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Rappel envoyé avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReminder(null);
    }
  };

  const getPriorityColor = (daysRemaining) => {
    if (daysRemaining <= 3) return 'bg-red-600 text-white border-red-200';
    if (daysRemaining <= 7) return 'bg-orange-600 text-white border-orange-800';
    if (daysRemaining <= 15) return 'bg-yellow-600 text-white border-yellow-800';
    return 'bg-blue-600 text-white border-blue-200';
  };

  const getPriorityBadge = (daysRemaining) => {
    if (daysRemaining <= 3) return <Badge className="bg-red-700 text-white font-medium">Critique</Badge>;
    if (daysRemaining <= 7) return <Badge className="bg-orange-700 text-white font-medium">Urgent</Badge>;
    if (daysRemaining <= 15) return <Badge className="bg-yellow-700 text-white font-medium">Attention</Badge>;
    return <Badge className="bg-blue-700 text-white font-medium">Info</Badge>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'subscription': return <CreditCard className="h-4 w-4" />;
      case 'insurance': return <FileCheck className="h-4 w-4" />;
      case 'membership': return <Users className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'subscription': return 'Souscription';
      case 'insurance': return 'Assurance';
      case 'membership': return 'Adhésion';
      default: return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Alertes Contrats Expirants
          </h2>
          <p className="text-gray-500">
            Contrats arrivant à expiration dans les prochains jours
          </p>
        </div>
        
        <div className="flex gap-2">
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value={7}>7 jours</option>
            <option value={15}>15 jours</option>
            <option value={30}>30 jours</option>
            <option value={60}>60 jours</option>
            <option value={90}>90 jours</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Tous les types</option>
            <option value="subscription">Souscriptions</option>
            <option value="insurance">Assurances</option>
            <option value="membership">Adhésions</option>
          </select>
          
          <Button variant="outline" onClick={loadExpiringContracts}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Cartes statistiques */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
              <div className="text-sm text-gray-500">Critique (≤3j)</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{stats.urgent}</div>
              <div className="text-sm text-gray-500">Urgent (≤7j)</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.warning}</div>
              <div className="text-sm text-gray-500">Attention (≤15j)</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.info}</div>
              <div className="text-sm text-gray-500">Info (&gt;15j)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Liste des contrats */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Aucun contrat expirant dans cette période</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {contracts.map((contract) => (
            <Card 
              key={contract.id} 
              className={`border-l-4 ${getPriorityColor(contract.days_remaining)}`}
            >
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${getPriorityColor(contract.days_remaining)}`}>
                      {getTypeIcon(contract.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{contract.title}</span>
                        {getPriorityBadge(contract.days_remaining)}
                        <Badge className="bg-gray-700 text-white font-medium">{getTypeLabel(contract.type)}</Badge>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {contract.user_name} • {contract.user_email}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Expire le {new Date(contract.expiry_date).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {contract.days_remaining} jour(s) restant(s)
                        </span>
                        {contract.amount && (
                          <span className="font-medium">
                            {contract.amount}€
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendReminder(contract.id, contract.type)}
                      disabled={sendingReminder === contract.id}
                    >
                      {sendingReminder === contract.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-1" />
                          Rappeler
                        </>
                      )}
                    </Button>
                    <Button size="sm">
                      Voir <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
